import {
	createContext,
	useContext,
	type Dispatch,
	type SetStateAction,
} from 'react';

import { AcceptDispatchOkI } from 'modules/acceptJs';
import { DialogMakePaymentStep } from './titles';
import { type ResolvedPaymentMethodI } from './usePaymentMethods';

export interface DialogMakePaymentFormValues {
	amount: string;
	payFullBalance: boolean;
	cardCode: string;
	cardNumber: string;
	expirationDate: string;
	/** Whether to save to the payment method for future use. Only relevant to Stripe flow */
	savePaymentMethod: boolean;
	/** How the user has chosen to pay */
	paymentMode: 'saved' | 'new' | 'paypal';
	/** id of the saved payment method chosen to pay with */
	paymentInstrumentId: string;
}

/** A Stripe payment confirmed at Stripe - the card is held - and not yet captured */
export interface HeldStripePaymentI {
	id: string;
	amount: string;
}

export type DialogMakePaymentFormErrors = Record<
	keyof DialogMakePaymentFormValues,
	string
>;

export interface DialogMakePaymentContextValue {
	/** what the account owes, as the payment amount is measured against it */
	accountBalanceDisplay: string;
	/** the new Stripe card entered before Continue, to be confirmed on Pay now */
	confirmationToken: string | null;
	formErrors: DialogMakePaymentFormErrors;
	formValues: DialogMakePaymentFormValues;
	/** neither the saved methods nor the profile standing in for them could be fetched */
	hasPaymentMethodsError: boolean;
	/** the card held by a Pay now whose capture failed, for the retry to capture */
	heldStripePayment: HeldStripePaymentI | null;
	isPayPalAllowed: boolean;
	isStripeAllowed: boolean;
	opaqueData: AcceptDispatchOkI['opaqueData'] | null;
	/** the saved methods a payment can be taken with, the account default first */
	paymentMethods: ResolvedPaymentMethodI[];
	step: DialogMakePaymentStep;
	clearSensitiveFormValues: () => void;
	onSubmitSuccess: () => void;
	onSubmitError: () => void;
	/** another go at fetching the saved methods, after they failed to arrive */
	refetchPaymentMethods: () => void;
	setConfirmationToken: Dispatch<SetStateAction<string | null>>;
	setHeldStripePayment: Dispatch<SetStateAction<HeldStripePaymentI | null>>;
	setFormErrors: Dispatch<SetStateAction<DialogMakePaymentFormErrors>>;
	setFormValues: Dispatch<SetStateAction<DialogMakePaymentFormValues>>;
	setOpaqueData: Dispatch<
		SetStateAction<AcceptDispatchOkI['opaqueData'] | null>
	>;
	setStep: (newStep: DialogMakePaymentStep) => void;
}

const dialogMakePaymentContext =
	createContext<DialogMakePaymentContextValue | null>(null);

export const useDialogMakePaymentContext = () => {
	const context = useContext(dialogMakePaymentContext);

	if (!context) {
		throw new Error(
			'`useDialogMakePaymentContext` must be used within a `dialogMakePaymentContext.Provider`',
		);
	}

	return context;
};

export default dialogMakePaymentContext;
